<template>
    <v-card class="defaultCard pa-3 h-100 d-flex flex-column" color="bgtopology" flat>
        <!-- PANEL SUPERIOR: FILTROS DE BÚSQUEDA -->
        <v-card color="#0d131f" class="pa-3 mb-3 pb-border-subtle" flat>
            <v-row class="ma-0 align-center ">
                <!-- FECHA -->
                <v-col cols="12" sm="6" md="3" class="pa-2">
                    <span class="pb-filter-label">Fecha</span>
                    <v-text-field
                        v-model="selectedDate"
                        type="date"
                        density="compact"
                        variant="outlined"
                        hide-details
                        class="pb-neon-input"
                    ></v-text-field>
                </v-col>

                <!-- TURNO -->
                <v-col cols="12" sm="6" md="3" class="pa-2">
                    <span class="pb-filter-label">Turno</span>
                    <v-select
                        v-model="selectedTurn"
                        :items="turnOptions"
                        item-title="title"
                        item-value="value"
                        density="compact"
                        variant="outlined"
                        hide-details
                        class="pb-neon-input"
                    ></v-select>
                </v-col>

                <!-- BUSCADOR MÚLTIPLE DE EQUIPOS CLIENTE POR NOMBRE O IP (MÁX. 10) -->
                <v-col cols="12" sm="6" md="3" class="pa-2">
                    <div class="d-flex align-center justify-space-between mb-1">
                        <span class="pb-filter-label">Equipos Cliente</span>
                        <span class="pb-count-badge" :class="{ 'pb-count-limit': selectedIps.length >= 10 }">
                            {{ selectedIps.length }} / 10 Máx.
                        </span>
                    </div>

                    <v-autocomplete
                        v-model="selectedIps"
                        :items="deviceOptions"
                        item-title="title"
                        item-value="value"
                        multiple
                        chips
                        closable-chips
                        clearable
                        density="compact"
                        variant="outlined"
                        hide-details
                        placeholder="Buscar camiones..."
                        no-data-text="No se encontraron camiones"
                        class="pb-neon-input"
                        @update:model-value="onSelectedIpsChange"
                    ></v-autocomplete>
                </v-col>

                <!-- BUSCADOR MÚLTIPLE DE EQUIPOS ACCESO POR NOMBRE O IP (MÁX. 10) -->
                <v-col cols="12" sm="6" md="3" class="pa-2">
                    <div class="d-flex align-center justify-space-between mb-1">
                        <span class="pb-filter-label">Equipos Acceso</span>
                        <span class="pb-count-badge" :class="{ 'pb-count-limit': selectedAccesoIps.length >= 10 }">
                            {{ selectedAccesoIps.length }} / 10 Máx.
                        </span>
                    </div>

                    <v-autocomplete
                        v-model="selectedAccesoIps"
                        :items="accesoOptions"
                        item-title="title"
                        item-value="value"
                        multiple
                        chips
                        closable-chips
                        clearable
                        density="compact"
                        variant="outlined"
                        hide-details
                        placeholder="Buscar equipos acceso..."
                        no-data-text="No se encontraron equipos de acceso"
                        class="pb-neon-input"
                        @update:model-value="onSelectedAccesoIpsChange"
                    ></v-autocomplete>
                </v-col>
            </v-row>

            <!-- BOTONES DE ACCIÓN -->
            <v-row class="ma-0 ga-3 mt-1">
                <v-col cols="12" class="pa-0 d-flex justify-end ga-2">
                    <v-btn
                        color="cyan-accent-3"
                        variant="flat"
                        size="small"
                        class="font-weight-black"
                        :loading="loadingData"
                        @click="loadHistory"
                    >
                        <v-icon start size="small">mdi-database-search</v-icon>
                        Cargar Datos
                    </v-btn>

                    <v-btn
                        color="success"
                        variant="outlined"
                        size="small"
                        class="font-weight-bold"
                        :disabled="telemetryPoints.length === 0"
                        @click="handleExportCSV"
                    >
                        <v-icon start size="small">mdi-file-delimited</v-icon>
                        Exportar CSV
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <!-- MAPA SATELITAL -->
        <div class="pb-map-wrapper flex-grow-1 position-relative">
            <div ref="mapContainerRef" id="mapPlaybackContainer" class="pb-map-element"></div>

            <!-- OVERLAY INFORMATIVO ANTES DE CARGAR DATOS -->
            <div v-if="telemetryPoints.length === 0 && !loadingData" class="pb-empty-overlay pa-4 text-center">
                <v-icon color="cyan-accent-3" size="large" class="mb-2">mdi-radar</v-icon>
                <div class="text-subtitle-2 font-weight-bold text-white">Consola de Replicación Histórica</div>
                <div class="text-caption text-grey-lighten-1 mt-1">
                    Selecciona hasta <b>10 equipos cliente</b> y/o <b>10 equipos acceso</b> por Nombre o IP, luego haz clic en <b>"Cargar Datos"</b>.
                </div>
            </div>

            <!-- RELOJ DIGITAL FLOTANTE SOBRE EL MAPA -->
            <div v-if="uniqueTimestamps.length > 0" class="pb-floating-clock pa-2 px-3">
                <div class="d-flex align-center ga-2">
                    <span class="pb-clock-live-dot"></span>
                    <span class="pb-clock-time">{{ currentFormattedTime }}</span>
                </div>
                <div class="pb-clock-info text-truncate">
                    Activos: <b>{{ activeEquipsCount }}</b> / {{ totalEquipsCount }}
                </div>
            </div>
        </div>

        <!-- REPRODUCTOR MULTIMEDIA INFERIOR -->
        <v-card color="#0d131f" class="pa-3 mt-3 pb-border-subtle" flat>
            <div class="d-flex flex-column ga-2">
                <!-- SLIDER DE LÍNEA DE TIEMPO -->
                <div class="d-flex align-center ga-3">
                    <span class="text-caption text-grey font-weight-bold pb-font-mono">
                        {{ formatTimeOnly(uniqueTimestamps[0]) }}
                    </span>

                    <v-slider
                        v-model="sliderIndex"
                        :min="0"
                        :max="maxSliderIndex"
                        :step="1"
                        hide-details
                        color="cyan-accent-3"
                        track-color="rgba(255,255,255,0.1)"
                        class="flex-grow-1"
                        :disabled="uniqueTimestamps.length === 0"
                        @update:model-value="onSliderChange"
                    ></v-slider>

                    <span class="text-caption text-grey font-weight-bold pb-font-mono">
                        {{ formatTimeOnly(uniqueTimestamps[maxSliderIndex]) }}
                    </span>
                </div>

                <!-- CONTROLES PLAY / PAUSA Y VELOCIDAD -->
                <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center ga-2">
                        <!-- REPLAY / REINICIAR -->
                        <v-btn
                            icon="mdi-rewind"
                            size="x-small"
                            variant="tonal"
                            color="grey"
                            :disabled="uniqueTimestamps.length === 0"
                            @click="restartPlayback"
                        ></v-btn>

                        <!-- BOTÓN PLAY / PAUSA -->
                        <v-btn
                            :icon="isPlaying ? 'mdi-pause' : 'mdi-play'"
                            color="cyan-accent-3"
                            size="small"
                            class="pb-glow-btn"
                            :disabled="uniqueTimestamps.length === 0"
                            @click="togglePlay"
                        ></v-btn>
                    </div>

                    <!-- SELECTOR DE VELOCIDAD -->
                    <div class="d-flex align-center ga-1">
                        <span class="text-caption text-grey-lighten-1 mr-2 font-weight-bold">Velocidad:</span>
                        <v-btn
                            v-for="speed in [1, 2, 5, 10, 20]"
                            :key="speed"
                            size="x-small"
                            :color="playbackSpeed === speed ? 'cyan-accent-3' : 'grey-darken-3'"
                            :variant="playbackSpeed === speed ? 'flat' : 'tonal'"
                            class="font-weight-black"
                            @click="setSpeed(speed)"
                        >
                            {{ speed }}x
                        </v-btn>
                    </div>
                </div>
            </div>
        </v-card>
    </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import camionIcon from '@/assets/Icons/camion.jpg';
import solartrailerIcon from '@/assets/Icons/solartrailer.jpg';
import CONSTMAPS from '@/constants/maps';
import { getTelemetryHistory, downloadTelemetryCSV, getInventoryDevices, getAccesoDevices, TelemetryPoint } from '@/services/Cementera/Telemetry';

const MAX_EQUIPMENT_LIMIT = 10;

// REFERENCIAS Y MAPAS DE DATOS
const mapContainerRef = ref<HTMLElement | null>(null);
let playbackMapInstance: maplibregl.Map | null = null;
let pbActiveMarkers: Map<string, maplibregl.Marker> = new Map();

// DICCIONARIO DE COLORES ASIGNADOS A CADA IP DE CAMIÓN
const ipColorMap = new Map<string, string>();

// ESTADOS DE FILTRO
const selectedDate = ref(new Date().toISOString().slice(0, 10));
const selectedTurn = ref('ALL');
const selectedIps = ref<string[]>([]);
const selectedAccesoIps = ref<string[]>([]);

// INVENTARIO COMPLETO
const allInventoryDevices = ref<{ ip: string; name: string }[]>([]);
const allAccesoDevices = ref<{ ip: string; name: string }[]>([]);

const loadingData = ref(false);
const isPlaying = ref(false);
const playbackSpeed = ref(5);
const sliderIndex = ref(0);
let animationTimer: ReturnType<typeof setInterval> | null = null;

const telemetryPoints = ref<TelemetryPoint[]>([]);

// OPCIONES DE TURNO
const turnOptions = [
    { title: 'Día Completo (24 hrs)', value: 'ALL' },
    { title: 'Turno Día (DDd 07:00-15:00)', value: 'DDd' },
    { title: 'Turno Tarde (DDa 15:00-23:00)', value: 'DDa' },
    { title: 'Turno Noche (DDn 23:00-07:00)', value: 'DDn' }
];

// LISTADO DE EQUIPOS COMPLETO PARA BÚSQUEDA
const deviceOptions = computed(() => {
    return allInventoryDevices.value.map(d => ({
        title: `${d.name || 'Equipo'} (${d.ip})`,
        value: d.ip
    }));
});

const accesoOptions = computed(() => {
    return allAccesoDevices.value.map(d => ({
        title: `${d.name || 'Equipo'} (${d.ip})`,
        value: d.ip
    }));
});

// LIMITAR LA SELECCIÓN MÁXIMA A 10 EQUIPOS
function onSelectedIpsChange(val: string[]) {
    if (val && val.length > MAX_EQUIPMENT_LIMIT) {
        selectedIps.value = val.slice(0, MAX_EQUIPMENT_LIMIT);
    }
}

function onSelectedAccesoIpsChange(val: string[]) {
    if (val && val.length > MAX_EQUIPMENT_LIMIT) {
        selectedAccesoIps.value = val.slice(0, MAX_EQUIPMENT_LIMIT);
    }
}

// COMPUTADOS DE TIEMPO
const uniqueTimestamps = computed(() => {
    const set = new Set<string>();
    telemetryPoints.value.forEach(p => set.add(p.fecha));
    return Array.from(set).sort();
});

const maxSliderIndex = computed(() => Math.max(0, uniqueTimestamps.value.length - 1));

const currentFormattedTime = computed(() => {
    if (uniqueTimestamps.value.length === 0) return '00:00:00';
    return uniqueTimestamps.value[sliderIndex.value] || '00:00:00';
});

const totalEquipsCount = computed(() => {
    const ips = new Set(telemetryPoints.value.map(p => p.ip));
    return ips.size;
});

const activeEquipsCount = computed(() => {
    const currentTs = uniqueTimestamps.value[sliderIndex.value];
    if (!currentTs) return 0;
    const active = telemetryPoints.value.filter(p => p.fecha === currentTs);
    return new Set(active.map(p => p.ip)).size;
});

function formatTimeOnly(dateStr?: string) {
    if (!dateStr) return '00:00';
    const parts = dateStr.split(' ');
    return parts.length >= 2 ? parts[1].substring(0, 5) : dateStr;
}

// CARGAR EL CATÁLOGO COMPLETO DE CAMIONES Y EQUIPOS DE ACCESO
async function loadDevicesList() {
    const [devices, accesoDevices] = await Promise.all([
        getInventoryDevices(),
        getAccesoDevices()
    ]);
    allInventoryDevices.value = devices;
    allAccesoDevices.value = accesoDevices;
}

// INICIALIZACIÓN DEL MAPA
function initPlaybackMap() {
    const containerEl = mapContainerRef.value || document.getElementById('mapPlaybackContainer');

    if (!containerEl || playbackMapInstance) return;

    try {
        playbackMapInstance = new maplibregl.Map({
            container: containerEl,
            center: [CONSTMAPS.longitud.value, CONSTMAPS.latitud.value],
            zoom: 14,
            pitch: 30,
            style: 'https://api.maptiler.com/maps/satellite/style.json?key=6JWVt6LFaY22nquimZpF'
        });

        playbackMapInstance.addControl(new maplibregl.NavigationControl(), 'top-right');

        playbackMapInstance.on('load', () => {
            setTimeout(() => {
                if (playbackMapInstance) {
                    playbackMapInstance.resize();
                }
            }, 150);
        });
    } catch (err) {
        console.error("Error al inicializar el mapa de Playback:", err);
    }
}

// CARGA MANUAL DE DATOS DE HISTORIAL
async function loadHistory() {
    pausePlayback();
    loadingData.value = true;

    // Construir lista de IPs a consultar
    const ipsToQuery: string[] = [];

    // Equipos cliente: si no hay selección, usar primeras 10 del inventario
    if (selectedIps.value.length > 0) {
        ipsToQuery.push(...selectedIps.value);
    } else if (allInventoryDevices.value.length > 0) {
        ipsToQuery.push(...allInventoryDevices.value.map(d => d.ip).slice(0, MAX_EQUIPMENT_LIMIT));
    }

    // Equipos acceso: solo si hay selección (es opcional)
    if (selectedAccesoIps.value.length > 0) {
        ipsToQuery.push(...selectedAccesoIps.value);
    }

    let targetIps: string | string[] = 'ALL';
    if (ipsToQuery.length > 0) {
        targetIps = ipsToQuery;
    }

    const res = await getTelemetryHistory(selectedDate.value, selectedTurn.value, targetIps);
    loadingData.value = false;

    if (res && res.data && res.data.length > 0) {
        let rawPoints = res.data;

        // Filtrar solo equipos válidos (cliente o acceso seleccionados)
        const validClientIps = new Set(allInventoryDevices.value.map(d => d.ip));
        const validAccesoIps = new Set(selectedAccesoIps.value);
        const validIps = new Set([...validClientIps, ...validAccesoIps]);
        rawPoints = rawPoints.filter(p => validIps.has(p.ip));

        telemetryPoints.value = rawPoints;
        sliderIndex.value = 0;
        await nextTick();
        drawRoutesOnMap();
        updateMarkersPosition();
    } else {
        telemetryPoints.value = [];
        clearMapLayers();
    }
}

function handleExportCSV() {
    downloadTelemetryCSV(selectedDate.value, selectedTurn.value);
}

// CAPAS Y DIBUJO DE RUTAS
function clearMapLayers() {
    pbActiveMarkers.forEach(m => m.remove());
    pbActiveMarkers.clear();
    ipColorMap.clear();

    if (!playbackMapInstance) return;

    try {
        const style = playbackMapInstance.getStyle();
        if (style && style.layers) {
            style.layers.forEach(layer => {
                if (layer.id.startsWith('pb-route-line-')) {
                    playbackMapInstance?.removeLayer(layer.id);
                }
            });
        }

        if (style && style.sources) {
            Object.keys(style.sources).forEach(sourceId => {
                if (sourceId.startsWith('pb-route-source-')) {
                    playbackMapInstance?.removeSource(sourceId);
                }
            });
        }
    } catch (e) {
        console.error("Error limpiando capas:", e);
    }
}

function drawRoutesOnMap() {
    if (!playbackMapInstance) return;
    clearMapLayers();

    const groupedByIp = new Map<string, [number, number][]>();

    telemetryPoints.value.forEach(p => {
        if (!groupedByIp.has(p.ip)) groupedByIp.set(p.ip, []);
        groupedByIp.get(p.ip)!.push([Number(p.longitud), Number(p.latitud)]);
    });

    const colors = [
        '#00E676', // Verde Neón
        '#00E5FF', // Cían Neón
        '#FFEA00', // Amarillo
        '#FF3D00', // Rojo Ciber
        '#AB47BC', // Púrpura
        '#29B6F6', // Azul Celeste
        '#EC407A', // Rosado
        '#26A69A', // Menta
        '#FFA726', // Naranja
        '#8D6E63'  // Marrón
    ];
    let colorIdx = 0;

    groupedByIp.forEach((coords, ip) => {
        if (coords.length < 2) return;

        const sourceId = `pb-route-source-${ip}`;
        const layerId = `pb-route-line-${ip}`;
        const color = colors[colorIdx % colors.length];

        // GUARDAR COLOR ASOCIADO A ESTA IP
        ipColorMap.set(ip, color);
        colorIdx++;

        playbackMapInstance?.addSource(sourceId, {
            type: 'geojson',
            data: {
                type: 'Feature',
                properties: { ip },
                geometry: { type: 'LineString', coordinates: coords }
            }
        });

        playbackMapInstance?.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.85 }
        });
    });

    if (telemetryPoints.value.length > 0) {
        const first = telemetryPoints.value[0];
        playbackMapInstance.flyTo({
            center: [Number(first.longitud), Number(first.latitud)],
            zoom: 14
        });
    }
}

function updateMarkersPosition() {
    if (!playbackMapInstance || uniqueTimestamps.value.length === 0) return;

    const currentTs = uniqueTimestamps.value[sliderIndex.value];
    if (!currentTs) return;

    const currentPoints = telemetryPoints.value.filter(p => p.fecha === currentTs);

    currentPoints.forEach(p => {
        const lng = Number(p.longitud);
        const lat = Number(p.latitud);
        
        // RECUPERAR EL COLOR ASIGNADO A ESTA IP (O CÍAN POR DEFECTO)
        const truckColor = ipColorMap.get(p.ip) || '#00E5FF';

        if (pbActiveMarkers.has(p.ip)) {
            pbActiveMarkers.get(p.ip)!.setLngLat([lng, lat]);
        } else {
            const isAcceso = selectedAccesoIps.value.includes(p.ip);
            const iconSrc = isAcceso ? solartrailerIcon : camionIcon;
            const iconAlt = isAcceso ? 'Equipo Acceso' : 'Camión';

            const el = document.createElement('div');
            el.className = 'pb-truck-marker';
            el.innerHTML = `
                <img src="${iconSrc}" class="pb-truck-img" alt="${iconAlt}" />
                <div class="pb-marker-name" style="color: ${truckColor}; border-color: ${truckColor}; text-shadow: 0 0 6px ${truckColor}aa;">
                    ${p.name || p.ip}
                </div>
            `;

            const marker = new maplibregl.Marker({ element: el })
                .setLngLat([lng, lat])
                .addTo(playbackMapInstance!);

            pbActiveMarkers.set(p.ip, marker);
        }
    });
}

// CONTROLES MULTIMEDIA
function togglePlay() {
    if (isPlaying.value) {
        pausePlayback();
    } else {
        startPlayback();
    }
}

function startPlayback() {
    if (uniqueTimestamps.value.length === 0) return;
    isPlaying.value = true;

    if (sliderIndex.value >= maxSliderIndex.value) {
        sliderIndex.value = 0;
    }

    const intervalMs = Math.max(50, 1000 / playbackSpeed.value);

    animationTimer = setInterval(() => {
        if (sliderIndex.value < maxSliderIndex.value) {
            sliderIndex.value++;
            updateMarkersPosition();
        } else {
            pausePlayback();
        }
    }, intervalMs);
}

function pausePlayback() {
    isPlaying.value = false;
    if (animationTimer) {
        clearInterval(animationTimer);
        animationTimer = null;
    }
}

function restartPlayback() {
    pausePlayback();
    sliderIndex.value = 0;
    updateMarkersPosition();
}

function setSpeed(speed: number) {
    playbackSpeed.value = speed;
    if (isPlaying.value) {
        pausePlayback();
        startPlayback();
    }
}

function onSliderChange() {
    updateMarkersPosition();
}

// CICLO DE VIDA DE VUE
onMounted(() => {
    loadDevicesList();
    nextTick(() => {
        setTimeout(() => {
            initPlaybackMap();
        }, 200);
    });
});

onUnmounted(() => {
    pausePlayback();
    clearMapLayers();
    if (playbackMapInstance) {
        playbackMapInstance.remove();
        playbackMapInstance = null;
    }
});
</script>

<style scoped>
.pb-border-subtle {
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
}

.pb-filter-label {
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    color: #00E5FF;
    letter-spacing: 0.5px;
    display: block;
}

.pb-count-badge {
    font-size: 0.60rem;
    font-weight: 800;
    color: #00E676;
    background: rgba(0, 230, 118, 0.1);
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid rgba(0, 230, 118, 0.25);
}

.pb-count-limit {
    color: #FFEA00;
    background: rgba(255, 234, 0, 0.1);
    border-color: rgba(255, 234, 0, 0.3);
}

:deep(.pb-neon-input .v-field) {
    background: rgba(13, 19, 31, 0.9) !important;
    border-radius: 6px !important;
    border: 1px solid rgba(0, 229, 255, 0.25) !important;
    font-size: 0.78rem !important;
}

:deep(.pb-neon-input .v-chip) {
    background: rgba(0, 229, 255, 0.15) !important;
    color: #00E5FF !important;
    border: 1px solid rgba(0, 229, 255, 0.3) !important;
    font-weight: 700 !important;
    font-size: 0.68rem !important;
    height: 20px !important;
}

.pb-map-wrapper {
    width: 100%;
    min-height: 50vh;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.pb-map-element {
    width: 100% !important;
    height: 100% !important;
}

.pb-empty-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    background: rgba(13, 19, 31, 0.88);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 229, 255, 0.3);
    border-radius: 12px;
    max-width: 380px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
}

.pb-floating-clock {
    position: absolute;
    top: 14px;
    left: 14px;
    z-index: 10;
    background: rgba(13, 19, 31, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0, 229, 255, 0.3);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
}

.pb-clock-time {
    font-family: monospace;
    font-size: 0.95rem;
    font-weight: 900;
    color: #00E5FF;
}

.pb-clock-info {
    font-size: 0.65rem;
    color: #94A3B8;
}

.pb-clock-live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #00E676;
    box-shadow: 0 0 8px #00E676;
}

.pb-glow-btn {
    box-shadow: 0 0 12px rgba(0, 229, 255, 0.4);
}

.pb-font-mono {
    font-family: monospace;
}

:deep(.pb-truck-marker) {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    user-select: none;
    transform: translate(-50%, -50%);
}

:deep(.pb-truck-img) {
    width: 32px;
    height: 32px;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.8));
    transition: transform 0.2s ease;
}

:deep(.pb-truck-marker:hover .pb-truck-img) {
    transform: scale(1.2);
}

:deep(.pb-marker-name) {
    font-family: monospace;
    font-weight: 800;
    font-size: 0.68rem;
    background-color: rgba(6, 9, 14, 0.85);
    padding: 1px 5px;
    border-radius: 4px;
    margin-top: 2px;
    border: 1px solid transparent;
    transition: all 0.2s ease;
}
</style>